// import hooks
import { useMemo } from 'react';

// import types
import { DutyCodePeriod } from 'generated/index';
import { DraftDutyLine, DutyLine } from 'RestAPIs/employees/hours-table/types';
import { DutyLineGridRow } from '../HoursTable.types';
import { UseRowsParams } from './useRows.types';

// import utils
import dayjs from 'dayjs';
import {
  generateMonthRows,
  generateWeekRows,
  isRowBlocked,
  isRowCrediting,
  isRowNominal,
  isRowRealized
} from '../HoursTable.utils';

export function useRows({
  selectedDate,
  dutyLines,
  extraRows,
  overrides,
  t,
  tRoot,
  period = 'month'
}: UseRowsParams): DutyLineGridRow[] {
  return useMemo(() => {
    // base calendar scaffold
    const calendarRows =
      period === 'week'
        ? generateWeekRows(dayjs(selectedDate))
        : generateMonthRows(dayjs(selectedDate));

    // group ALL duty-lines by date ‐ nothing gets lost
    const dutiesByDate = dutyLines.reduce<Record<string, DutyLine[]>>(
      (map, duty) => {
        const key = dayjs(duty.dutyDate).format('YYYY-MM-DD');
        (map[key] ||= []).push(duty);
        return map;
      },
      {}
    );

    // group locally-added drafts by day – unchanged
    const draftsByDate = extraRows.reduce<Map<string, DraftDutyLine[]>>(
      (acc, draft) => {
        (
          acc.get(draft.dateKey) ??
          acc.set(draft.dateKey, []).get(draft.dateKey)!
        ).push(draft);
        return acc;
      },
      new Map()
    );

    // flatten
    return calendarRows.flatMap((calRow) => {
      const dutiesForDay = dutiesByDate[calRow.dateKey] ?? [];

      // if the API returned nothing, we still want one empty calendar row
      const rowsForDay = dutiesForDay.length ? dutiesForDay : [undefined];

      const materializedRows = rowsForDay.map((duty) => {
        const hasOrgUnit = duty?.brId != null;
        const hasWorkAgreement = duty?.workAgreementNr != null;

        const base: DutyLineGridRow = duty
          ? {
              ...calRow,
              ...duty,
              id: duty.dutyLineId,
              requestId: duty.dutyLineId,
              dvb: duty.workAgreementNr,
              organizationalUnit: duty.brId,
              dutyCodeGeneral: duty.dutyCode,
              dutyCodeGeneralOrganizationalUnit: duty.displayDutyCode,
              periods: duty.periods ?? [],
              dutyTime: duty.totalHours.toFixed(2),
              dutyType: duty.periods
                .map(
                  (period: DutyCodePeriod) =>
                    period.dutyHourType && period.dutyHourType.code
                )
                .join(', '),
              credit: '',
              statusApproval: duty.flags.includes('APPROVED')
                ? t('dutyHistory.status.APPROVED')
                : '',
              status: tRoot(`employeeBonuses.StatusSvc.${duty.statusSvc}`),
              error: duty.calculationError && t(duty.calculationError),
              canBeDeleted:
                (hasOrgUnit || isRowNominal(duty)) &&
                hasWorkAgreement &&
                !isRowRealized(duty) &&
                !isRowCrediting(duty) &&
                !isRowBlocked(duty),
              isNominalRow: duty.flags.includes('HOURS_NOMINAL') ?? false
            }
          : calRow; // empty scaffold row

        // overlay copy-paste overrides (unchanged logic)
        return overrides.has(base.id)
          ? { ...base, ...overrides.get(base.id)! }
          : base;
      });

      // finally add any local drafts for that date
      return [...materializedRows, ...(draftsByDate.get(calRow.dateKey) ?? [])];
    });
  }, [selectedDate, dutyLines, extraRows, overrides, t, tRoot]);
}
